import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { resetBulkState, resetUsersV3, UserV3 } from "../../../store/slices/UsersV3Slice";
import { fetchUserOrganizationAsync } from "../../../store/thunks/OrganizationsV3Thunk";
import {
  fetchAccessibleOrganizationsAsync,
  fetchUsersV3Async,
  fetchUsersV3StatsAsync,
} from "../../../store/thunks/UsersV3Thunk";
import { UserListFilters } from "../../../services/UsersV3Service";
import {
  canManageUsers,
  canViewUsers,
  isOrgScopedRole,
  USERS_PER_PAGE,
  USERS_ROLE_LABELS,
  UserSortKey,
} from "../../../constants/users";
import { canViewOrganizations } from "../../../constants/organizations";
import { Button } from "../../Catalyst/button";
import { PaginationNext, PaginationPrevious } from "../../Catalyst/pagination";
import StatCard from "../../common/StatCard";
import SupportIcon from "../../../assets/SupportIcon";
import GlobalSearchField from "../Organizations/GlobalSearchField";
import { formatStatValue } from "../Organizations/utils";
import GlobalUsersTable from "./GlobalUsersTable";
import UserFilters from "./UserFilters";
import BulkActionBar from "./BulkActionBar";
import MoveUsersDialog from "./MoveUsersDialog";
import DeactivateUsersDialog from "./DeactivateUsersDialog";
import SupportDialog from "./SupportDialog";
import {
  EMPTY_SELECTION,
  getSelectedCount,
  getSingleSourceOrganization,
  nextSort,
  RowSelection,
  setPageSelected,
  toApiSelection,
  toggleRow,
  UserSort,
} from "./utils";

const EMPTY_FILTERS: UserListFilters = { role: [], status: [], organizationId: null };

const LoadingState = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="h-8 w-8 animate-spin text-zinc-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  </div>
);

interface GlobalUserListProps {
  userRole: string | null;
  // The signed-in user's own organization; names the page title.
  userOrganizationId: number | null;
}

// ADM-89: every user the signed-in admin can see, across all orgs. Org-scoped
// lists live under the hierarchy at /organizations/:orgId/users (ADM-5).
const GlobalUserList = ({ userRole, userOrganizationId }: GlobalUserListProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const canManage = canManageUsers(userRole);

  const {
    users,
    page,
    totalPages,
    totalItems,
    listStatus,
    listError,
    stats,
    organizationOptions,
    organizationOptionsStatus,
  } = useSelector((state: RootState) => state.UsersV3Store);
  const userOrganizationName = useSelector(
    (state: RootState) => state.OrganizationsV3Store.userOrganizationName
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<UserListFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<UserSort>(null);
  const [selection, setSelection] = useState<RowSelection>(EMPTY_SELECTION);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  // Bumped on every open so each bulk dialog mounts with fresh state.
  const [bulkDialogKey, setBulkDialogKey] = useState(0);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  useEffect(() => {
    if (userOrganizationId != null && !userOrganizationName) {
      dispatch(fetchUserOrganizationAsync(userOrganizationId));
    }
  }, [userOrganizationId, userOrganizationName, dispatch]);

  const roleLabel = (userRole && USERS_ROLE_LABELS[userRole]) || "";
  // Org Admin counts cover their org and its sub-orgs only, so the card copy
  // must not claim "all orgs" for them.
  const statsScope = isOrgScopedRole(userRole) ? "across your organizations" : "across all orgs";
  const welcomeTitle = userOrganizationName
    ? `Welcome back, ${[userOrganizationName, roleLabel].filter(Boolean).join(" ")}`
    : "Welcome back";

  const fetchList = useCallback(() => {
    dispatch(fetchUsersV3Async({ page: currentPage, perPage: USERS_PER_PAGE, filters, sort }));
  }, [currentPage, filters, sort, dispatch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    dispatch(fetchUsersV3StatsAsync());
    dispatch(fetchAccessibleOrganizationsAsync());
  }, [dispatch]);

  // If the current page no longer exists (e.g. a move emptied the last page of
  // an org-filtered list), step back to the last page that does.
  useEffect(() => {
    if (listStatus === STATUS.SUCCESS && users.length === 0 && currentPage > 1) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [listStatus, users.length, currentPage, totalPages]);

  useEffect(() => {
    return () => {
      dispatch(resetUsersV3());
    };
  }, [dispatch]);

  // A filter or sort change redefines the result set: back to page 1, and drop
  // the selection so a bulk action never applies to rows the admin cannot see.
  const handleFiltersChange = (next: UserListFilters) => {
    setFilters(next);
    setCurrentPage(1);
    setSelection(EMPTY_SELECTION);
  };

  const handleSort = (key: UserSortKey) => {
    setSort((current) => nextSort(current, key));
    setCurrentPage(1);
    setSelection(EMPTY_SELECTION);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setCurrentPage(nextPage);
  };

  const handleViewActivity = (user: UserV3) => {
    if (!user.organization) return;
    navigate(`/user-profile/${user.organization.id}/${user.id}`, {
      state: { backTo: "/user-list-dashboard", backLabel: "Back to all users" },
    });
  };

  // The org hierarchy pages are not open to Org Admins yet, so their org names
  // render as plain text instead of a link to an "Access restricted" screen.
  const handleOpenOrganization = useMemo(
    () =>
      canViewOrganizations(userRole)
        ? (organizationId: number) => navigate(`/organizations/${organizationId}`)
        : null,
    [userRole, navigate]
  );

  const openBulkDialog = (open: (value: boolean) => void) => {
    dispatch(resetBulkState());
    setBulkDialogKey((key) => key + 1);
    open(true);
  };

  const handleBulkCompleted = () => {
    setSelection(EMPTY_SELECTION);
    fetchList();
    dispatch(fetchUsersV3StatsAsync());
  };

  const selectedCount = getSelectedCount(selection, totalItems);
  const apiSelection = toApiSelection(selection, filters);
  const sourceOrganization = getSingleSourceOrganization(selection);

  const isLoading = listStatus === STATUS.LOADING || listStatus === STATUS.IDLE;
  const isFailed = listStatus === STATUS.FAILED;
  const isEmpty = listStatus === STATUS.SUCCESS && users.length === 0;
  const hasResults = listStatus === STATUS.SUCCESS && users.length > 0;
  const hasFilters =
    filters.role.length > 0 || filters.status.length > 0 || filters.organizationId != null;

  const renderTableBody = () => {
    if (isLoading) return <LoadingState />;

    if (isFailed) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
          <p className="max-w-md text-center text-sm font-medium text-red-500">
            {listError || "Something went wrong while loading users. Please try again."}
          </p>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-20">
          <p className="text-sm font-medium text-texthead opacity-70">No users found</p>
          <p className="max-w-md text-center text-sm text-textColor">
            {hasFilters ? "There are no users matching these filters." : "There are no users yet."}
          </p>
        </div>
      );
    }

    return (
      <div className="px-6">
        <GlobalUsersTable
          users={users}
          sort={sort}
          onSort={handleSort}
          selectable={canManage}
          selection={selection}
          onToggleRow={(user) => setSelection((current) => toggleRow(current, user))}
          onTogglePage={(selected) => setSelection((current) => setPageSelected(current, users, selected))}
          onOpenOrganization={handleOpenOrganization}
          onViewActivity={handleViewActivity}
        />
      </div>
    );
  };

  return (
    // Bottom padding keeps the last row and pagination clear of the floating bulk bar.
    <div className={`p-8 ${canManage && selectedCount > 0 ? "pb-28" : ""}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="desktext10 min-w-0 truncate">{welcomeTitle}</h1>
        <div className="flex shrink-0 items-center gap-4">
          <GlobalSearchField className="hidden w-[360px] md:block xl:w-[490px]" />
          <Button outline onClick={() => setIsSupportOpen(true)} aria-label="Open support form">
            <SupportIcon />
            Support
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Active Users"
          description={`Total active users ${statsScope}`}
          value={formatStatValue(stats?.activeUsers)}
          valueLabel="active users"
        />
        <StatCard
          title="Signed Up Users"
          description="Signed up but not finished onboarding"
          value={formatStatValue(stats?.signedUpUsers)}
          valueLabel="signed up users"
        />
        <StatCard
          title="Pending Invites"
          description="Invited but haven't signed up yet"
          value={formatStatValue(stats?.invitedUsers)}
          valueLabel="invited users"
        />
        <StatCard
          title="Deactivated Users"
          description={`Total deactivated users ${statsScope}`}
          value={formatStatValue(stats?.deactivatedUsers)}
          valueLabel="deactivated users"
        />
      </div>

      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-4">
          <div className="desktext11 text-texthead">Users</div>
          <UserFilters
            filters={filters}
            onChange={handleFiltersChange}
            organizationOptions={organizationOptions}
            isOrganizationOptionsLoading={organizationOptionsStatus === STATUS.LOADING}
            isOrganizationOptionsError={organizationOptionsStatus === STATUS.FAILED}
          />
        </div>

        {renderTableBody()}

        {hasResults && (
          <div className="flex items-center justify-between border-t border-shadowborder px-6 py-3">
            <span className="text-sm font-medium text-textColor3">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-3">
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </div>
          </div>
        )}
      </div>

      {canManage && selectedCount > 0 && (
        <BulkActionBar
          selectedCount={selectedCount}
          isAllPagesSelected={selection.mode === "allMatching"}
          onToggleAllPages={(selected) =>
            setSelection(
              selected
                ? { mode: "allMatching", excludedIds: [] }
                : setPageSelected(EMPTY_SELECTION, users, true)
            )
          }
          onMove={() => openBulkDialog(setIsMoveOpen)}
          onDeactivate={() => openBulkDialog(setIsDeactivateOpen)}
          onDismiss={() => setSelection(EMPTY_SELECTION)}
        />
      )}

      {/* Kept mounted after the selection clears so a result step can still show. */}
      {canManage && (
        <>
          <MoveUsersDialog
            key={`move-${bulkDialogKey}`}
            open={isMoveOpen}
            onClose={() => setIsMoveOpen(false)}
            selection={apiSelection}
            selectedCount={selectedCount}
            sourceOrganization={sourceOrganization}
            onCompleted={handleBulkCompleted}
          />
          <DeactivateUsersDialog
            key={`deactivate-${bulkDialogKey}`}
            open={isDeactivateOpen}
            onClose={() => setIsDeactivateOpen(false)}
            selection={apiSelection}
            selectedCount={selectedCount}
            onCompleted={handleBulkCompleted}
          />
        </>
      )}

      <SupportDialog open={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </div>
  );
};

const UserListDashboard = () => {
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);

  // userAccount is fetched in AppLayout; treat its absence as "still loading".
  if (!userAccount) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (!canViewUsers(userAccount.user_role)) {
    return (
      <div className="p-8">
        <div className="mt-10 flex flex-col items-center justify-center gap-3 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
          <div className="desktext14 text-texthead">Access restricted</div>
          <p className="max-w-md text-sm text-textColor">
            You do not have permission to view users. Please contact an administrator if you
            believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <GlobalUserList
      userRole={userAccount.user_role}
      userOrganizationId={userAccount.organization_id ? Number(userAccount.organization_id) : null}
    />
  );
};

export default UserListDashboard;
